/**
 * ESAL Modal Management Hook
 *
 * Dedicated hook for ESAL modal state management using FormStateManagementService.
 * Extracts ESAL modal logic from InputForm component for improved organization
 * and testability while maintaining identical behavior.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { FormStateManagementService, EsalModalState } from '../services/FormStateManagementService';
import { DynamicFormikProps } from '../types/FormTypes';
import { logDebug, logError } from '../utils/utils';

// Hook return type for ESAL modal management
interface UseEsalModalManagementReturn {
    isEsalModalOpen: boolean;
    esalModalKey: number;
    handleEsalModalClose: () => void;
    handleEsalCalculationComplete: (result: number | { esalFinalValue: number; totalPassagesPerYear: number }) => void;
    openEsalModal: (formikRef: DynamicFormikProps) => void;
}

/**
 * Custom hook for ESAL modal management using FormStateManagementService
 *
 * This hook centralizes all ESAL modal-related state and behavior that was
 * previously scattered in the InputForm component. It provides the same
 * interface while using the service for better state management.
 *
 * @returns Object with ESAL modal state and handler functions
 */
export const useEsalModalManagement = (): UseEsalModalManagementReturn => {
    // Get singleton service instance
    const formStateService = useMemo(() => FormStateManagementService.getInstance(), []);

    // Local state synchronized with service
    const [esalModalState, setEsalModalState] = useState<EsalModalState>(() => formStateService.getEsalModalState());

    // Subscribe to service ESAL modal state changes
    useEffect(() => {
        const unsubscribe = formStateService.subscribeToEsalModal(newState => {
            setEsalModalState(newState);
            logDebug('[useEsalModalManagement]', 'ESAL modal state updated from service', newState);
        });

        // Initialize with current service state
        setEsalModalState(formStateService.getEsalModalState());

        // Cleanup modal state on unmount (replicate InputForm cleanup logic)
        return () => {
            // Close modal if open when component unmounts
            const currentState = formStateService.getEsalModalState();
            if (currentState.isOpen) {
                formStateService.closeEsalModal();
            }
            unsubscribe();
        };
    }, [formStateService]);

    // Memoized handler to open ESAL modal
    const openEsalModal = useCallback(
        (formikRef: DynamicFormikProps) => {
            formStateService.openEsalModal(formikRef);
            logDebug('[useEsalModalManagement]', 'ESAL modal opened with formik ref');
        },
        [formStateService]
    );

    // Memoized handler to close ESAL modal (replicates handleEsalModalClose from InputForm)
    const handleEsalModalClose = useCallback(() => {
        formStateService.closeEsalModal();
        logDebug('[useEsalModalManagement]', 'ESAL modal closed by user');
    }, [formStateService]);

    // Memoized handler for ESAL calculation completion (replicates handleEsalCalculationComplete)
    const handleEsalCalculationComplete = useCallback(
        (result: number | { esalFinalValue: number; totalPassagesPerYear: number }) => {
            const success = formStateService.completeEsalCalculation(result);

            if (success) {
                const resultStr = typeof result === 'number' ? result : JSON.stringify(result);
                logDebug('[useEsalModalManagement]', `ESAL calculation completed with result: ${resultStr}`);
            } else {
                const resultStr = typeof result === 'number' ? result : JSON.stringify(result);
                logError('[useEsalModalManagement]', `Failed to complete ESAL calculation with result: ${resultStr}`);
            }
        },
        [formStateService]
    );

    return {
        isEsalModalOpen: esalModalState.isOpen,
        esalModalKey: esalModalState.modalKey,
        handleEsalModalClose,
        handleEsalCalculationComplete,
        openEsalModal,
    };
};

export default useEsalModalManagement;
